/**
 * Admin utilities for role management
 * 
 * This file contains helper functions for managing user roles.
 * These should only be used by administrators.
 */

import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { app } from '../lib/firebase';

/**
 * Update a user's role in Firestore
 * @param userId - The Firebase Auth UID of the user
 * @param role - The role to assign ('user' or 'admin')
 */
export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
  try {
    const db = getFirestore(app);
    const userDocRef = doc(db, 'users', userId);
    
    await updateDoc(userDocRef, {
      Role: role
    });
    
    console.log(`Successfully updated user ${userId} role to ${role}`);
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
}

/**
 * Helper function to make the current user an admin
 * This can be called from the browser console for testing
 */
export async function makeCurrentUserAdmin(): Promise<void> {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth(app);
  
  if (!auth.currentUser) {
    throw new Error('No user is currently logged in');
  }
  
  await updateUserRole(auth.currentUser.uid, 'admin');
  console.log('Current user has been made an admin. Please refresh the page to see changes.');
}

// Make functions available in development for console access
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.makeCurrentUserAdmin = makeCurrentUserAdmin;
  // @ts-ignore
  window.updateUserRole = updateUserRole;
  console.log('Admin utilities available: makeCurrentUserAdmin(), updateUserRole(userId, role)');
}